import { NgClass } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap/nav';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PasConfirmationService,
  PasLayoutSidebarHolderComponent,
  PasSidebarHandleService,
  PasTabContentDirective,
  PasTabDirective,
  PasTabsetComponent,
  PasTabTitleDirective,
} from '@porsche-aftersales/frontend-toolbox';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { Subscription } from 'rxjs';

import { AGGridExcelService } from '../../services/ag-grid/excel-service';
import { AGGridFormatterService } from '../../services/ag-grid/formatter-service';
import { CampaignRoutingService } from '../../services/details-view-service/campaign-routing.service';
import { NotificationServiceWrapper } from '../../services/notification-service-wrapper/notification-service-wrapper';
import { RecourseCampaignStore } from '../../services/recourse-campaign/recourse-campaign-store.service';
import { TableViewService } from '../../services/reporting/table-view.service';
import { UserService } from '../../services/user-service/user.service';
import { AG_DATE_COL_CONFIG } from '../../shared/ag-grid/ag-grid-functions';
import { AgGridService } from '../../shared/ag-grid/ag-grid.service';
import { TableViewAdministrationState } from '../../sub-component/table-view-administration/common/enum/table-view-administration-state.enum';
import { TableViewCustomModel } from '../../sub-component/table-view-administration/tva-custom/common/model/table-view-custom-model';
import { TvaCustomSelectionComponent } from '../../sub-component/table-view-administration/tva-custom/component-selection/tva-custom-selection.component';
import {
  NOTIFICATION_UNEXPECTED_ERROR,
  TvaCustomSidebarComponent,
} from '../../sub-component/table-view-administration/tva-custom/component-sidebar/tva-custom-sidebar.component';
import { TvaCustomService } from '../../sub-component/table-view-administration/tva-custom/tva-custom.service';
import { TvaSessionService } from '../../sub-component/table-view-administration/tva-session/tva-session.service';

import { RecourseCampaignDetailsComponent } from './recourse-campaign-details/recourse-campaign-details.component';
import { ButtonEditRendererComponent } from './renderer/button-edit-renderer.component';

@Component({
  selector: 'app-recourse-campaign',
  styleUrls: ['./recourse-campaign.component.scss'],
  templateUrl: './recourse-campaign.component.html',
  imports: [
    PasTabsetComponent,
    PasTabDirective,
    TvaCustomSelectionComponent,
    TranslatePipe,
    AgGridAngular,
    RecourseCampaignDetailsComponent,
    PasLayoutSidebarHolderComponent,
    TvaCustomSidebarComponent,
    NgClass,
    PasTabTitleDirective,
    PasTabContentDirective,
  ],
})
export class RecourseCampaignComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly agGridService = inject(AgGridService);
  private readonly agGridFormatterService = inject(AGGridFormatterService);
  private readonly agGridExcelService = inject(AGGridExcelService);
  private readonly recourseCampaingStore = inject(RecourseCampaignStore);
  private readonly pasConfirmationService = inject(PasConfirmationService);
  private readonly userService = inject(UserService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly campaignRoutingService = inject(CampaignRoutingService);
  private readonly tableViewService = inject(TableViewService);
  private readonly tvaCustomService = inject(TvaCustomService);
  private readonly tvaSessionService = inject(TvaSessionService);

  @ViewChild(PasTabsetComponent)
  public tabset: PasTabsetComponent;

  @ViewChild(RecourseCampaignDetailsComponent)
  public aktionsRegressDetailsComponent: RecourseCampaignDetailsComponent;

  public readonly recourseCampaignOverviewTab: string = 'recoursecampaign-overview-tab';
  public readonly recourseCampaignEditTab: string = 'recoursecampaign-edit-tab';

  public gridOptions: GridOptions;
  public columnDefs: any;
  public rowData: any;
  public defaultColDef: any;
  public excelStyles: any;
  public multiSortKey: any;
  public frameworkComponents: any;
  public detailsTabDisabled = true;
  public detailsTabHidden = true;
  public isCreateExcelRecourseCampaignEnabled = true;
  public tooltipShowDelay = 0;
  public numberOfButtonsVisibleSidebar = 1;
  public campaignsOverviewData: any[] = [];
  public listTableViews: TableViewCustomModel[] = [];

  private gridApi: any;

  private cancelToEditEventSubscription: Subscription;

  public constructor() {
    this.frameworkComponents = {
      buttonEditRenderer: ButtonEditRendererComponent,
    };
  }

  public ngOnInit(): void {
    this.gridOptions = {
      rowHeight: 35,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      getLocaleText: this.agGridService.translate(this.userService, this.translateService),
      overlayComponentParams: {
        noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
      },
    };

    this.defaultColDef = {
      resizable: true,
      sortable: true,
      unSortIcon: true,
      floatingFilter: true,
    };

    this.translateService.get('RECOURSE_CAMPAIGN_OVERVIEW_TAB').subscribe(() => {
      this.columnDefs = [
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_CAMPAIGN_RECOURSE_ID'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_CAMPAIGN_RECOURSE_ID'),
          field: 'campaignRecourseId',
          filter: 'agTextColumnFilter',
          minWidth: 75,
          sort: 'asc',
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_OPERATION_ID'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_OPERATION_ID'),
          field: 'operationId',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOPIC'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_TOPIC'),
          field: 'topic',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_NAME1'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_NAME1'),
          field: 'name1',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_SUPPLIER'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_SUPPLIER'),
          field: 'supplier',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_ASSIGNEE'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_ASSIGNEE'),
          field: 'assigneeFullName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_ORDER_TYPE'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_ORDER_TYPE'),
          field: 'orderType',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_CAMPAIGN_COSTS_PROGNOSIS'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_CAMPAIGN_COSTS_PROGNOSIS'),
          field: 'campaignCostsPrognosis',
          valueFormatter: this.agGridFormatterService.currencyFormatterDefault(this.userService),
          filter: 'agTextColumnFilter',
          cellStyle: { textAlign: 'right' },
          cellClass: 'currencyFormat',
          filterParams: {
            textFormatter: val => {
              if (val == null) {
                return null;
              }
              return val.toString();
            },
          },
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_RECOURSE_ACTUAL_COSTS'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_RECOURSE_ACTUAL_COSTS'),
          field: 'recourseActualCosts',
          valueFormatter: this.agGridFormatterService.currencyFormatterDefault(this.userService),
          filter: 'agTextColumnFilter',
          cellStyle: { textAlign: 'right' },
          cellClass: 'currencyFormat',
          filterParams: {
            textFormatter: val => {
              if (val == null) {
                return null;
              }
              return val.toString().replace(',', '.');
            },
          },
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOTAL_REVENUE'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_TOTAL_REVENUE'),
          field: 'totalRevenue',
          valueFormatter: this.agGridFormatterService.currencyFormatterDefault(this.userService),
          filter: 'agTextColumnFilter',
          cellStyle: { textAlign: 'right' },
          cellClass: 'currencyFormat',
          filterParams: {
            textFormatter: val => {
              if (val == null) {
                return null;
              }
              return val.toString().replace(',', '.');
            },
          },
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_FULFILLMENT_DEGREE'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_FULFILLMENT_DEGREE'),
          field: 'fulfillmentDegree',
          valueFormatter: this.agGridFormatterService.percentageFormatter(this.userService),
          filter: 'agTextColumnFilter',
          cellClass: 'percentFormat',
          filterParams: {
            textFormatter: val => {
              if (val == null) {
                return null;
              }
              return val.toString().replace(',', '.');
            },
          },
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_FKCAMPAIGN_STATE_ID'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_FKCAMPAIGN_STATE_ID'),
          valueGetter: this.agGridService.getCampaignStateAndTranslateDescription(this.translateService),
          filter: 'agSetColumnFilter',
          filterParams: {
            suppressSelectAll: false,
            suppressMiniFilter: true,
          },
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_CAMPAIGN_STATE_TYPE'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_CAMPAIGN_STATE_TYPE'),
          valueGetter: this.agGridService.translateCampaignStateTypeCell(this.translateService),
          filter: 'agSetColumnFilter',
          filterParams: {
            suppressSelectAll: false,
            suppressMiniFilter: true,
          },
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_CAMPAIGN_ORIGINAL'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_CAMPAIGN_ORIGINAL'),
          valueGetter: this.agGridService.getCampaignOriginalYesAndNoTranslation(this.translateService),
          filter: 'agSetColumnFilter',
          filterParams: {
            suppressSelectAll: false,
            suppressMiniFilter: true,
          },
        },
        {
          headerName: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_CREATE_DATE'),
          headerTooltip: this.translateService.instant('RECOURSE_CAMPAIGN_TABLE_TOOLTIP_CREATE_DATE'),
          ...AG_DATE_COL_CONFIG,
          field: 'campaignCreateDate',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          cellStyle: { textAlign: 'right' },
          minWidth: 155,
          cellClass: 'dateFormat',
        },
        {
          colId: 'buttonColumn',
          headerName: '',
          headerClass: ['ag-header-last-cell'],
          minWidth: 50,
          maxWidth: 50,
          field: 'value',
          cellRenderer: 'buttonEditRenderer',
          filter: false,
          cellRendererParams: {
            onClick: this.onBtnEdit.bind(this),
          },
          menuTabs: [],
          unSortIcon: false,
        },
      ];
    });

    this.multiSortKey = 'ctrl';

    this.recourseCampaingStore.getCurrentUserRights();

    this.excelStyles = this.agGridExcelService.getExcelStyles(this.translateService);

    this.cancelToEditEventSubscription = this.campaignRoutingService
      .cancelToEditEventListener()
      .subscribe(sentObject => {
        // TODO return to previously selected campaign
        if (sentObject?.campaignRecourseId !== null) {
          const obj: { rowData: { campaignRecourseId: string; recourseCampaignExId: string } } = {
            rowData: {
              campaignRecourseId: sentObject.campaignRecourseId,
              recourseCampaignExId: sentObject.recourseCampaignExId,
            },
          };
          this.onBtnEdit(obj);
        }
      });
  }

  public enableSidebar(): void {
    this.sidebarHandleService.enableSidebarForView();
  }

  public async onGridReady(event: GridReadyEvent): Promise<void> {
    this.enableSidebar();
    this.gridApi = event.api;
    this.gridApi.updateGridOptions({
      headerHeight: 32,
    });

    const defaultSortModel = [
      {
        colId: 'campaignCreateDate',
        sort: 'desc',
      },
    ];
    this.gridApi.applyColumnState(defaultSortModel);

    this.tableViewService
      .getTableViewsFromRegisteredUserByTableViewArea(TableViewAdministrationState.CampaignOverview)
      .subscribe({
        next: async resultListTableView => {
          this.listTableViews = resultListTableView;
          this.campaignsOverviewData = await this.recourseCampaingStore.getRecourseCampaigns();
          event.api.setGridOption('rowData', this.campaignsOverviewData);
          this.recourseCampaingStore.getCurrentUserRights();

          this.tvaSessionService.init(
            TableViewAdministrationState.CampaignOverview,
            false,
            this.gridOptions,
            this.tvaCustomService,
            this.gridApi,
          );

          this.tvaCustomService.init(
            TableViewAdministrationState.CampaignOverview,
            this.listTableViews,
            false,
            this.gridOptions,
            this.gridApi,
          );
          if (!this.tvaSessionService.isTableViewFromTableViewSessionAvailable) {
            this.tvaCustomService.doSelectedTableViewOnComponent();
          }
        },
        error: error => {
          this.tvaCustomService.tvaCustomSidebarComponent.showNotification(NOTIFICATION_UNEXPECTED_ERROR, null);
          // eslint-disable-next-line no-console
          console.log(error);
        },
      });
  }

  public getContextMenuItems(): any[] {
    return [];
  }

  public onChangingTab(event: NgbNavChangeEvent): void {
    if (event.activeId === this.recourseCampaignEditTab && event.nextId === this.recourseCampaignOverviewTab) {
      event.preventDefault(); // Also have a look to 'stayOnEdit()'
      this.onBtnRecourseCampaignEditCancel();
    }
  }

  private async onBtnEdit(event): Promise<void> {
    if (event.rowData.recourseCampaignExId) {
      if (
        await this.recourseCampaingStore.getRecourseCampaignExDetails(
          event.rowData.campaignRecourseId,
          event.rowData.recourseCampaignExId,
        )
      ) {
        this.openTab(this.recourseCampaignEditTab);
      }
    } else {
      if (await this.recourseCampaingStore.getRecourseCampaignDetails(event.rowData.campaignRecourseId)) {
        this.openTab(this.recourseCampaignEditTab);
      }
    }
  }

  private openTab(tabId: string): void {
    this.tabset.activeId = tabId;
    this.setTabVisibility(tabId);
    this.tabset.select(tabId);
    this.tabset.changeTabSize();
  }

  private setTabVisibility(tabId: string): void {
    if (tabId === this.recourseCampaignOverviewTab) {
      this.campaignRoutingService.emitCancelToEditEvent(null, null);
      this.detailsTabDisabled = true;
      this.detailsTabHidden = true;
    }
    if (tabId === this.recourseCampaignEditTab) {
      this.campaignRoutingService.emitCancelToEditEvent(null, null);
      this.detailsTabDisabled = false;
      this.detailsTabHidden = false;
    }
  }

  public onBtnRecourseCampaignEditCancel(): void {
    if (this.aktionsRegressDetailsComponent.formValueChanged) {
      this.preventTabChange();
    } else {
      this.switchToOverview();
    }
  }

  public onBtnCreateExcelRecourseCampaign(): void {
    try {
      this.createExcelRecourseCampaign();
    } catch {
      const title: string = this.translateService.instant('EXCEL_EXPORT_ERROR_TITLE');
      const content: string = this.translateService.instant('EXCEL_EXPORT_ERROR_CONTENT');
      this.notificationServiceWrapper.addErrorNotification(title, content);
    }
  }

  public createExcelRecourseCampaign(): void {
    this.gridApi.exportDataAsExcel(
      this.agGridExcelService.getExcelFrame(
        this.translateService.instant('RECOURSE_CAMPAIGN_OVERVIEW_TAB'),
        this.translateService.instant('EXCEL_EXPORT_CAMPAIGN_RECOURSE_OVERVIEW'),
        this.gridOptions,
      ),
    );
  }

  /**
   * If User select a campaign which assignee is not existing anymore or right was removed:
   * When opening campaign details show message: 'Invalid assignee. Please select a valid assignee.'
   * Click 'Cancel': User will go back to Campaign Overview page
   * Click 'Ok': show Campaign Detail view
   * assigne dropdown should be empty and  red marked 'Assigend'
   */
  public onCheckRecourseCampaignEditAssignedPerson(): void {
    this.translateService
      .get([
        'RECOURSE_CAMPAIGN_DETAIL_DIALOG_INVALID_ASSIGNEE_TITLE',
        'RECOURSE_CAMPAIGN_DETAIL_DIALOG_INVALID_ASSIGNEE_MESSAGE',
        'OK',
        'CANCEL',
      ])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.RECOURSE_CAMPAIGN_DETAIL_DIALOG_INVALID_ASSIGNEE_TITLE;
        const message: string = translate.RECOURSE_CAMPAIGN_DETAIL_DIALOG_INVALID_ASSIGNEE_MESSAGE;
        const ok: string = translate.OK;
        const dismiss: string = translate.CANCEL;

        this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, false).then(
          () => {
            this.stayOnEdit();
          },
          () => {
            this.switchToOverview();
          },
        );
      });
  }

  private preventTabChange(): void {
    this.translateService
      .get(['FORM_CONTAINS_UNSAVED_DATA_TITLE', 'FORM_CONTAINS_UNSAVED_DATA_MESSAGE', 'OK', 'CANCEL'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.FORM_CONTAINS_UNSAVED_DATA_TITLE;
        const message: string = translate.FORM_CONTAINS_UNSAVED_DATA_MESSAGE;
        const ok: string = translate.OK;
        const dismiss: string = translate.CANCEL;

        this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, false).then(
          () => {
            this.switchToOverview();
          },
          () => {
            this.stayOnEdit();
          },
        );
      });
  }

  private switchToOverview(): void {
    this.openTab(this.recourseCampaignOverviewTab);
  }

  /*
   * This is necessary to be able to cancel an edit-process serveral times through the Tab-Item.
   * Because of 'event.preventDefault();' we need to register the current state after this call so the tab-item stay clickable.
   */
  private stayOnEdit(): void {
    this.openTab(this.recourseCampaignEditTab);
  }
}
